import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Phone, Shield, Award } from "lucide-react";
import { Button } from "./ui/button";
import { Breadcrumbs } from "./Breadcrumbs";

/**
 * PageHero - shared hero used across all service / content pages.
 * Different layout from the Home hero but uses identical typography & colours.
 */
export const PageHero = ({
  eyebrow,
  h1,
  highlight,
  subheading,
  breadcrumbs,
  image,
  trustBadges = true,
}) => {
  const bgImage =
    image ||
    "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=1920&q=80";

  return (
    <section
      className="relative pt-28 pb-14 md:pt-40 md:pb-24 overflow-hidden"
      data-testid="page-hero"
    >
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url('${bgImage}')` }}
      />
      <div className="absolute inset-0 bg-navy/90" />

      <div className="relative z-10 container mx-auto px-6 md:px-12 lg:px-20">
        {breadcrumbs && (
          <div className="mb-8">
            <Breadcrumbs items={breadcrumbs} />
          </div>
        )}

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="max-w-4xl"
        >
          {eyebrow && (
            <span className="inline-block bg-gold/20 text-gold px-4 py-2 rounded-full text-sm font-semibold mb-6">
              {eyebrow}
            </span>
          )}

          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
            {h1}{" "}
            {highlight && <span className="text-gold">{highlight}</span>}
          </h1>

          <p className="text-lg md:text-xl text-slate-300 leading-relaxed mb-10 max-w-2xl">
            {subheading}
          </p>

          <div className="flex flex-col sm:flex-row gap-4 mb-10">
            <Link to="/get-a-quote">
              <Button
                size="lg"
                className="bg-gold hover:bg-gold-dark text-navy font-bold text-base px-7 py-6 rounded-xl w-full sm:w-auto"
                data-testid="hero-quote-btn"
              >
                Get a Quote
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <a href="tel:+442080363586" className="w-full sm:w-auto">
              <Button
                size="lg"
                variant="outline"
                className="border-white/30 text-white hover:bg-white/10 font-semibold text-base px-7 py-6 rounded-xl w-full sm:w-auto"
                data-testid="hero-call-btn"
              >
                <Phone className="mr-2 w-5 h-5" />
                Call 020 8036 3586
              </Button>
            </a>
          </div>

          {trustBadges && (
            <div className="flex flex-wrap gap-5 md:gap-8">
              {[
                { icon: Shield, label: "Fully Insured", value: "£5M Cover" },
                { icon: Award, label: "Re-Clean Guarantee", value: "7-day" },
              ].map((b) => (
                <div key={b.label} className="flex items-center gap-3">
                  <div className="w-11 h-11 bg-white/10 rounded-xl flex items-center justify-center">
                    <b.icon className="w-5 h-5 text-teal" />
                  </div>
                  <div>
                    <p className="text-white font-semibold text-sm">{b.value}</p>
                    <p className="text-slate-400 text-xs">{b.label}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </motion.div>
      </div>
    </section>
  );
};
